import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sheshdash_theme_mode';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function readStoredMode(): ThemeMode {
  if (!isBrowser) return 'system';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // ignore (private mode / disabled storage)
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (!isBrowser || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyMode(mode: ThemeMode): void {
  if (!isBrowser) return;
  const isDark = mode === 'dark' || (mode === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', isDark);
}

export function useTheme(): {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  cycleMode: () => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [isDark, setIsDark] = useState<boolean>(() =>
    mode === 'dark' || (mode === 'system' && systemPrefersDark()),
  );

  useEffect(() => {
    applyMode(mode);
    setIsDark(mode === 'dark' || (mode === 'system' && systemPrefersDark()));

    if (!isBrowser || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (mode === 'system') {
        applyMode('system');
        setIsDark(mql.matches);
      }
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (!isBrowser) return;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const cycleMode = useCallback(() => {
    setMode(mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system');
  }, [mode, setMode]);

  return { mode, isDark, setMode, cycleMode };
}

export function initThemeBeforeRender(): void {
  applyMode(readStoredMode());
}
